import { ProductCategory as ProductCategoryEnum, SessionStorageKeys } from '@aglenergy/shared-library';
import { Component, ComponentInterface, getAssetPath, h, Host, JSX, Prop, State } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { configService } from 'app/shared/services/config/config.service';
import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';
import { CustomerProfile } from 'app/shared/models/customerProfile';
import { locationService } from 'app/shared/services/location/location-service';
import { getDataSpaSearchParams } from 'app/shared/utilities/helper-functions';
import { DataSpaKey } from 'app/shared/models/dataSpa';
import { DataSpaSearchParams } from 'app/shared/utilities/data-spa-search-params.util';
import { EnergySearchStore } from 'app/shared/models/current-search-request.model';

@Component({
  tag: 'moving-header-info',
  styleUrl: 'moving-header-info.scss',
  shadow: false,
  scoped: true
})
export class MovingHeaderInfo implements ComponentInterface {
  @Prop() public readonly dataSpa: string = null;

  @State() private customerProfile: CustomerProfile;
  @State() private movingAddress = null;
  @State() private isMobileMoversLandingPage = false;

  private spaParams: DataSpaSearchParams;

  public async componentWillLoad(): Promise<void> {
    if (this.dataSpa) {
      this.spaParams = getDataSpaSearchParams(this.dataSpa);
      this.isMobileMoversLandingPage = this.spaParams.get(DataSpaKey.isMobile) === 'true';
    }

    if (!this.isMobileMoversLandingPage) {
      this.customerProfile = customerProfileService.getCustomerProfile();
      const productSessionData = productsSessionStorageService.getLatestProductsByProductCategory('nbn');
      const productSearchResponses: EnergySearchStore[] =
        JSON.parse(sessionStorage.getItem(SessionStorageKeys.productSearchResponse)) ?? ([] as EnergySearchStore[]);
      const searchRequest = productSearchResponses.find((response) => response?.request?.addressString);
      await configService.loadConfig('/apps/comparison');
      store.set(StoreKeys.prefillAddress, true);
      this.movingAddress = productSessionData?.key || searchRequest?.request?.addressString;

      store.onChange(StoreKeys.productDetails, () => {
        const updatedProductSessionData = productsSessionStorageService.getLatestProductsByProductCategory('nbn');
        this.movingAddress = updatedProductSessionData?.key;

        if (this.movingAddress === 'default') {
          locationService.navigateToMoversEligibility();
        }
      });

      tealiumAnalyticService.trackPrefillAddress('Address Validated', 'Prefill Address Validated Successfully', ProductCategoryEnum.Nbn);
    }
  }

  public onButtonClick(): void {
    if (this.isMobileMoversLandingPage && this.hasNbn()) {
      locationService.navigateToNbnMoving();
    } else if (!this.isMobileMoversLandingPage && this.hasMobile()) {
      locationService.navigateToMobileMoving();
    } else {
      locationService.navigateToCart();
    }
  }

  render(): JSX.Element {
    return (
      <Host>
        <div class="moving-header">
          <div class="moving-header__container">
            <div class="moving-header__selection">
              <div class="moving-header__selection__message">
                <h2>Select your {this.isMobileMoversLandingPage ? 'mobile' : 'nbn'} plan</h2>
              </div>
              {(!this.customerProfile?.hasExistingNbn || this.isMobileMoversLandingPage) && (
                <div class="moving-header__selection__continue">
                  <div class="moving-header__selection__continue__container">
                    <div class="moving-header__selection__continue__message">
                      <agl-ds-button
                        id="continue-button"
                        type="tertiary"
                        input-type="button"
                        srContext={this.getContinueButtonText()}
                        size="sm"
                        icon-path={getAssetPath('../assets/icon/icon-chevron-right.svg')}
                        icon-size="xs"
                        icon-position="right"
                        onClick={() => this.onButtonClick()}
                      >
                        {this.getContinueButtonText()}
                      </agl-ds-button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!this.isMobileMoversLandingPage && (
              <div class="moving-header__address">
                <div class="moving-header__address__container">
                  <div class="moving-header__address__plan-available">Showing plans available at</div>
                  <div class="moving-header__address__info">{this.movingAddress}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }

  private getContinueButtonText(): string {
    return `Continue without ${this.isMobileMoversLandingPage ? 'mobile' : 'internet'}`;
  }

  private hasMobile(): boolean {
    return this.customerProfile?.isMobileUpdated;
  }

  private hasNbn(): boolean {
    return this.customerProfile?.isInternetUpdated;
  }
}
